import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, X, Rocket, PartyPopper, ArrowRight } from "lucide-react";

export const OnboardingChecklist = ({ refreshKey }) => {
  const [data, setData] = useState(null);

  const load = () => api.get("/onboarding").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  if (!data || data.dismissed) return null;

  const dismiss = async () => {
    setData({ ...data, dismissed: true });
    try { await api.post("/onboarding/dismiss"); } catch { /* */ }
  };

  const pct = Math.round((data.done / data.total) * 100);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="hud-card accent-top rounded-lg p-5 relative"
        style={{ "--accent-line": data.complete ? "#10B981" : "#06B6D4" }}
        data-testid="onboarding-checklist"
      >
        <button data-testid="onboarding-dismiss" onClick={dismiss} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200 transition-colors">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5">
          {data.complete ? <PartyPopper className="w-5 h-5 text-emerald-400" /> : <Rocket className="w-5 h-5 text-cyan-400" />}
          <div>
            <div className="font-display font-bold text-sm" data-testid="onboarding-title">
              {data.complete ? "You're all set — fleet is live 🎉" : "Get your fleet live"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-0.5" data-testid="onboarding-progress">
              {data.done} of {data.total} complete
            </div>
          </div>
        </div>

        {/* progress bar */}
        <div className="mt-3 h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>

        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {data.steps.map((s) => (
            <div
              key={s.key}
              data-testid={`onboarding-step-${s.key}`}
              className={`rounded-md border p-3 transition-colors ${s.done ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-slate-800 bg-[#080B11]"}`}
            >
              <div className="flex items-start gap-2">
                {s.done
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-testid={`onboarding-check-${s.key}`} />
                  : <Circle className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className={`text-[13px] font-medium ${s.done ? "text-slate-400 line-through" : "text-slate-200"}`}>{s.title}</div>
                  {!s.done && <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{s.desc}</div>}
                </div>
              </div>
              {!s.done && (
                <Link to={s.link} data-testid={`onboarding-cta-${s.key}`} className="mt-2.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors">
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
